import { useQuery } from "@tanstack/react-query";

import {
  getGetWebsiteEmbedSettingsQueryKey,
  getWebsiteEmbedSettings,
} from "@/api/orval/ageless-platform-features/ageless-platform-features";
import type {
  GetSharedWebsiteEmbedSettingsParams,
  GetWebsiteEmbedSettingsParams,
} from "@/api/orval/model";
import {
  getGetSharedWebsiteEmbedSettingsQueryKey,
  getSharedWebsiteEmbedSettings,
} from "@/api/orval/ageless-marketing-setup-link/ageless-marketing-setup-link";
import { isAuthorized } from "@/core/auth/auth";

export type AgelessWebsiteEmbedSettings = {
  readonly enabled: boolean;
  readonly allowedOrigins: readonly string[];
};

/**
 * The platform's website-embed state, read through whichever door the current viewer has.
 *
 * A signed-in admin reads the admin endpoint. A visitor on the login-free marketing page
 * reads the anonymous mirror on the marketing-share controller — the admin endpoint's
 * controller carries `AdminUserAuthorize`, so it stays 401 for them by design.
 */
export function useAgelessWebsiteEmbedSettings(
  platformId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): AgelessWebsiteEmbedSettings | undefined {
  const isAuthed = isAuthorized();

  const args = { platformId: platformId ?? "" };

  const { data } = useQuery({
    queryKey: isAuthed
      ? (({ platformId, ...params }: { platformId: string } & GetWebsiteEmbedSettingsParams) =>
          getGetWebsiteEmbedSettingsQueryKey(platformId, params))(args)
      : (({
          platformId,
          ...params
        }: { platformId: string } & GetSharedWebsiteEmbedSettingsParams) =>
          getGetSharedWebsiteEmbedSettingsQueryKey(platformId, params))(args),
    queryFn: () =>
      isAuthed
        ? (({ platformId, ...params }: { platformId: string } & GetWebsiteEmbedSettingsParams) =>
            getWebsiteEmbedSettings(platformId, params))(args)
        : (({
            platformId,
            ...params
          }: { platformId: string } & GetSharedWebsiteEmbedSettingsParams) =>
            getSharedWebsiteEmbedSettings(platformId, params))(args),
    enabled: enabled && Boolean(platformId),
    retry: false,
  });

  return data;
}
